import { Chip, ChipProps, Typography, css, styled } from "@mui/material";
import { FC } from "react";

import { text } from "@/modules/irohana-components/theme/colors";
import { typography } from "@/modules/irohana-components/theme/typography";
import { rounded } from "@/modules/irohana-components/theme/variables";
import { hexToRgba } from "@/modules/irohana-components/utils/pieces";

export type TTagVariant = "bold" | "light";

export type TTagProps = {
  variant?: TTagVariant;
  color: string;
  textColor?: string;
  title: string;
  fontWeight?: number;
} & Omit<ChipProps, "variant" | "color">;

export const Tag: FC<TTagProps> = ({ variant = "bold", color, title, textColor, fontWeight, ...rest }) => {
  return (
    <StyledChip
      chipVariant={variant}
      backgroundColor={color}
      label={
        <StyledTypography>
          {variant === "light" && <StyledPoint background={color} />}
          <Typography variant="cap12" style={{ color: textColor, fontWeight: fontWeight }}>
            {title}
          </Typography>
        </StyledTypography>
      }
      {...rest}
    />
  );
};

const options = { shouldForwardProp: (propName: string) => !["chipVariant", "backgroundColor"].includes(propName) };

const StyledChip = styled(Chip, options)<{ chipVariant: TTagVariant; backgroundColor: string }>`
  ${({ chipVariant }) => (chipVariant === "bold" ? BoldTagCSS : LightTagCSS)};
  background-color: ${({ chipVariant, backgroundColor }) => (chipVariant === "bold" ? backgroundColor : hexToRgba(backgroundColor, 0.2))};
`;

const BoldTagCSS = css`
  border-radius: ${rounded.md};
  .MuiTypography-root {
    font-size: 12px;
    font-weight: bold;
    color: ${text.white};
  }
`;

const LightTagCSS = css`
  border-radius: ${rounded.lg};
  .MuiTypography-root {
    font-size: 14px;
    color: ${text.primary};
  }
`;

const StyledTypography = styled("span")`
  display: inline-flex;
  align-items: center;
  column-gap: 8px;
  ${css(typography.body14)}
`;

const StyledPoint = styled("span")<{ background: string }>`
  width: 8px;
  height: 8px;
  border-radius: ${rounded.full};
  background-color: ${({ background }) => background};
`;
